import type { FileIndex } from "./files.ts";
import { fileIcon } from "./icons.ts";

/** One replacement in a file: the text at `from` must still be `expect`. */
export interface ReplaceEdit {
    from: number;
    to: number;
    expect: string;
    insert: string;
}

export interface SearchHost {
    /** The file's text, as edited if it is open. */
    text(path: string): Promise<string | null>;
    open(path: string, line: number, column: number, preview: boolean): void;
    /** A guest path as the results show it. */
    display(path: string): { name: string; folder: string; relative: string };
    /** Whether edits to the file are refused, as they are in the bundled ESP-IDF tree. */
    readOnly(path: string): boolean;
    /** Apply the edits whose text is still as expected; resolves to how many were applied. */
    replace(path: string, edits: ReplaceEdit[]): Promise<number>;
    confirm(text: string, action: string): Promise<boolean>;
    /** Tell the user about a replace that failed or fell short, with the error if there is one. */
    failed(text: string, error?: unknown): void;
}

export interface Match {
    line: number;
    column: number;
    length: number;
    /** Where the match starts in the file's text. */
    offset: number;
    text: string;
}

/** What the last run searched for; updates to the results use the same. */
interface Query {
    pattern: RegExp;
    regex: boolean;
    included: (path: string) => boolean;
    excluded: (path: string) => boolean;
}

interface FileResult {
    matches: Match[];
    element: HTMLElement;
}

const MAX_MATCHES = 5000;
const MAX_FILE_BYTES = 2 << 20;
/** How long the results wait after an edit or a change on disk before searching the file again. */
const UPDATE_MS = 300;

/**
 * Find in files: the side bar's Search view. It reads the files of the file
 * index through the runtime worker, a few at a time, and draws each file's
 * matches as they are found. The three toggles are VS Code's: match case,
 * whole word, regular expression.
 *
 * The results follow changes: `refresh` searches the given files again with
 * the last query, and a changed directory lists the files again. With the
 * replace field open, each match shows its replacement next to the struck-out
 * text; that is the preview of what Replace All writes.
 */
export class SearchView {
    readonly element: HTMLElement;
    readonly #input: HTMLInputElement;
    readonly #replace: HTMLInputElement;
    readonly #replaceRow: HTMLElement;
    readonly #replaceToggle: HTMLButtonElement;
    readonly #replaceAll: HTMLButtonElement;
    readonly #include: HTMLInputElement;
    readonly #exclude: HTMLInputElement;
    readonly #summary: HTMLElement;
    readonly #results: HTMLElement;
    readonly #toggles: Record<"case" | "word" | "regex", HTMLButtonElement>;
    readonly #index: FileIndex;
    readonly #host: SearchHost;
    #generation = 0;
    #timer: number | undefined;
    #query: Query | undefined;
    #searching = false;
    readonly #files = new Map<string, FileResult>();
    readonly #collapsed = new Set<string>();
    readonly #pending = new Set<string>();
    #relist = false;
    #updateTimer: number | undefined;

    constructor(index: FileIndex, host: SearchHost) {
        this.#index = index;
        this.#host = host;
        this.element = document.createElement("div");
        this.element.className = "wb-search";
        const box = document.createElement("div");
        box.className = "wb-search-box";
        this.#replaceToggle = document.createElement("button");
        this.#replaceToggle.type = "button";
        this.#replaceToggle.className = "wb-search-replace-toggle codicon codicon-chevron-right";
        this.#replaceToggle.title = "Toggle Replace";
        this.#replaceToggle.setAttribute("aria-expanded", "false");
        this.#replaceToggle.addEventListener("click", () => this.toggleReplace());
        this.#input = textInput("Search");
        const toggle = (icon: string, title: string) => {
            const button = document.createElement("button");
            button.type = "button";
            button.className = `codicon codicon-${icon}`;
            button.title = title;
            button.setAttribute("aria-pressed", "false");
            button.addEventListener("click", () => {
                button.setAttribute("aria-pressed", String(button.getAttribute("aria-pressed") !== "true"));
                this.run();
            });
            return button;
        };
        this.#toggles = {
            case: toggle("case-sensitive", "Match Case"),
            word: toggle("whole-word", "Match Whole Word"),
            regex: toggle("regex", "Use Regular Expression"),
        };
        const field = document.createElement("div");
        field.className = "wb-input-with-toggles";
        field.append(this.#input, this.#toggles.case, this.#toggles.word, this.#toggles.regex);
        this.#replace = textInput("Replace");
        this.#replaceAll = document.createElement("button");
        this.#replaceAll.type = "button";
        this.#replaceAll.className = "codicon codicon-replace-all";
        this.#replaceAll.title = "Replace All";
        this.#replaceAll.disabled = true;
        this.#replaceAll.addEventListener("click", () => void this.#replaceEverything());
        this.#replaceRow = document.createElement("div");
        this.#replaceRow.className = "wb-input-with-toggles wb-search-replace";
        this.#replaceRow.hidden = true;
        this.#replaceRow.append(this.#replace, this.#replaceAll);
        this.#include = textInput("files to include (e.g. *.c, main/)");
        this.#exclude = textInput("files to exclude (e.g. *.md, test/)");
        const fields = document.createElement("div");
        fields.className = "wb-search-fields";
        fields.append(field, this.#replaceRow, this.#include, this.#exclude);
        box.append(this.#replaceToggle, fields);
        this.#summary = document.createElement("div");
        this.#summary.className = "wb-search-summary";
        this.#results = document.createElement("div");
        this.#results.className = "wb-search-results";
        this.element.append(box, this.#summary, this.#results);
        for (const input of [this.#input, this.#include, this.#exclude]) {
            input.addEventListener("input", () => {
                clearTimeout(this.#timer);
                this.#timer = window.setTimeout(() => this.run(), 250);
            });
            input.addEventListener("keydown", (event) => {
                if (event.key === "Enter") this.run();
            });
        }
        // A new replacement changes only how the matches are drawn.
        this.#replace.addEventListener("input", () => this.#redraw());
        this.#replace.addEventListener("keydown", (event) => {
            // VS Code's key for Replace All.
            if (event.key === "Enter" && event.altKey && (event.ctrlKey || event.metaKey))
                void this.#replaceEverything();
        });
    }

    focus(text?: string): void {
        if (text) this.#input.value = text;
        this.#input.focus();
        this.#input.select();
        if (text) this.run();
    }

    /** Show or hide the replace field; `open` forces one or the other. */
    toggleReplace(open = this.#replaceRow.hidden): void {
        this.#replaceRow.hidden = !open;
        this.#replaceToggle.setAttribute("aria-expanded", String(open));
        this.#replaceToggle.className = `wb-search-replace-toggle codicon codicon-chevron-${open ? "down" : "right"}`;
        if (open) this.#replace.focus();
        this.#redraw();
    }

    run(): void {
        void this.#search(++this.#generation);
    }

    /**
     * Search `paths` again with the last query, after a short pause that
     * gathers a burst of edits. `relist` also lists the files again, for a
     * file created, deleted or renamed.
     */
    refresh(paths: readonly string[], relist = false): void {
        if (this.#query === undefined) return;
        for (const path of paths) this.#pending.add(path);
        this.#relist ||= relist;
        clearTimeout(this.#updateTimer);
        this.#updateTimer = window.setTimeout(() => void this.#update(), UPDATE_MS);
    }

    get #replacing(): boolean {
        return !this.#replaceRow.hidden;
    }

    #pattern(): RegExp | undefined {
        const query = this.#input.value;
        if (query === "") return undefined;
        const on = (name: "case" | "word" | "regex") => this.#toggles[name].getAttribute("aria-pressed") === "true";
        let source = on("regex") ? query : query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        if (on("word")) source = `\\b${source}\\b`;
        try {
            return new RegExp(source, on("case") ? "g" : "gi");
        } catch {
            return undefined;
        }
    }

    async #search(generation: number): Promise<void> {
        const pattern = this.#pattern();
        this.#results.replaceChildren();
        this.#files.clear();
        this.#pending.clear();
        this.#relist = false;
        this.#query = undefined;
        this.#replaceAll.disabled = true;
        if (pattern === undefined) {
            this.#summary.textContent = this.#input.value === "" ? "" : "Invalid pattern";
            return;
        }
        const query: Query = {
            pattern,
            regex: this.#toggles.regex.getAttribute("aria-pressed") === "true",
            included: includeMatcher(this.#include.value),
            excluded: excludeMatcher(this.#exclude.value),
        };
        this.#query = query;
        this.#searching = true;
        this.#summary.textContent = "Searching…";
        const files = (await this.#index.files()).filter((path) => this.#inScope(query, path));
        let total = 0;
        const queue = [...files];
        const worker = async () => {
            while (queue.length > 0 && total < MAX_MATCHES) {
                const path = queue.shift()!;
                const matches = await this.#find(query, path);
                if (generation !== this.#generation) return;
                total += matches.length;
                this.#show(path, matches);
            }
        };
        await Promise.all(Array.from({ length: 8 }, worker));
        if (generation !== this.#generation) return;
        this.#searching = false;
        this.#summarize();
        // Changes reported during the search wait for it.
        if (this.#pending.size > 0 || this.#relist) this.refresh([]);
    }

    async #update(): Promise<void> {
        const query = this.#query;
        if (query === undefined) return;
        if (this.#searching) return; // the search's end calls refresh again
        const generation = this.#generation;
        const paths = new Set(this.#pending);
        this.#pending.clear();
        const relist = this.#relist;
        this.#relist = false;
        // The index leaves out the build tree, whose files a build reports as changed.
        const files = new Set(await this.#index.files());
        if (generation !== this.#generation) return;
        if (relist) {
            for (const path of this.#files.keys()) if (!files.has(path)) paths.add(path);
            for (const path of files) if (!this.#files.has(path) && this.#inScope(query, path)) paths.add(path);
        }
        for (const path of paths) {
            const matches = files.has(path) && this.#inScope(query, path) ? await this.#find(query, path) : [];
            if (generation !== this.#generation) return;
            this.#show(path, matches);
        }
        this.#summarize();
    }

    #inScope(query: Query, path: string): boolean {
        const relative = this.#host.display(path).relative;
        return query.included(relative) && !query.excluded(relative);
    }

    async #find(query: Query, path: string): Promise<Match[]> {
        const text = await this.#host.text(path);
        if (text === null || text.length > MAX_FILE_BYTES || text.includes("\u0000")) return [];
        return find(text, query.pattern);
    }

    /** Draw a file's matches in its place in path order, or remove the file when it has none. */
    #show(path: string, matches: Match[]): void {
        const old = this.#files.get(path);
        if (matches.length === 0) {
            old?.element.remove();
            this.#files.delete(path);
            return;
        }
        const element = this.#fileResult(path, matches);
        if (old !== undefined) old.element.replaceWith(element);
        else {
            let next: HTMLElement | null = null;
            for (const [other, result] of this.#files) {
                if (other > path && (next === null || other < next.dataset.path!)) next = result.element;
            }
            this.#results.insertBefore(element, next);
        }
        this.#files.set(path, { matches, element });
    }

    #redraw(): void {
        for (const [path, result] of this.#files) this.#show(path, result.matches);
    }

    #summarize(): void {
        if (this.#searching) return;
        let total = 0;
        for (const { matches } of this.#files.values()) total += matches.length;
        const fileCount = this.#files.size;
        this.#replaceAll.disabled = total === 0;
        this.#summary.textContent =
            total === 0
                ? "No results found."
                : `${total}${total >= MAX_MATCHES ? "+" : ""} result${total === 1 ? "" : "s"} in ${fileCount} file${fileCount === 1 ? "" : "s"}`;
    }

    #replacement(match: Match): string {
        return replacementFor(match, this.#query!.pattern, this.#replace.value, this.#query!.regex);
    }

    /** Replace the listed matches of `path`, or only `only`, and search the file again. */
    async #replaceIn(path: string, only?: Match): Promise<number> {
        const result = this.#files.get(path);
        if (result === undefined || this.#host.readOnly(path)) return 0;
        const matches = only === undefined ? result.matches : [only];
        const edits = matches.map((match) => ({
            from: match.offset,
            to: match.offset + match.length,
            expect: match.text.slice(match.column - 1, match.column - 1 + match.length),
            insert: this.#replacement(match),
        }));
        try {
            return await this.#host.replace(path, edits);
        } catch (e) {
            this.#host.failed(`Could not replace in ${this.#host.display(path).relative}`, e);
            return 0;
        } finally {
            this.#pending.add(path);
            void this.#update();
        }
    }

    async #replaceEverything(): Promise<void> {
        if (this.#query === undefined || this.#searching) return;
        const paths = [...this.#files.keys()].filter((path) => !this.#host.readOnly(path));
        const total = paths.reduce((sum, path) => sum + this.#files.get(path)!.matches.length, 0);
        if (total === 0) return;
        const occurrences = `${total} occurrence${total === 1 ? "" : "s"} across ${paths.length} file${paths.length === 1 ? "" : "s"}`;
        if (!(await this.#host.confirm(`Replace ${occurrences} with '${this.#replace.value}'?`, "Replace"))) return;
        const done = await Promise.all(paths.map((path) => this.#replaceIn(path)));
        const replaced = done.reduce((sum, n) => sum + n, 0);
        if (replaced < total) {
            this.#host.failed(`Replaced ${replaced} of ${total} occurrences; the others changed since the search`);
        }
    }

    #dismiss(path: string, match?: Match): void {
        const result = this.#files.get(path);
        if (result === undefined) return;
        this.#show(path, match === undefined ? [] : result.matches.filter((m) => m !== match));
        this.#summarize();
    }

    #fileResult(path: string, matches: Match[]): HTMLElement {
        const group = document.createElement("div");
        group.className = "wb-search-file";
        group.dataset.path = path;
        const header = document.createElement("div");
        header.className = "wb-row wb-search-file-header";
        const { name, folder } = this.#host.display(path);
        const chevron = document.createElement("span");
        const label = document.createElement("span");
        label.className = "wb-row-label";
        label.textContent = name;
        const description = document.createElement("span");
        description.className = "wb-row-description";
        description.textContent = folder;
        const count = document.createElement("span");
        count.className = "wb-count-badge";
        count.textContent = String(matches.length);
        const writable = this.#replacing && !this.#host.readOnly(path);
        const actions = rowActions([
            ...(writable ? [action("replace-all", "Replace All", () => void this.#replaceIn(path))] : []),
            action("close", "Dismiss", () => this.#dismiss(path)),
        ]);
        header.append(chevron, fileIcon(path), label, description, actions, count);
        const list = document.createElement("div");
        const collapse = (hidden: boolean) => {
            list.hidden = hidden;
            chevron.className = `wb-twistie codicon codicon-chevron-${hidden ? "right" : "down"}`;
        };
        collapse(this.#collapsed.has(path));
        header.addEventListener("click", () => {
            if (list.hidden) this.#collapsed.delete(path);
            else this.#collapsed.add(path);
            collapse(!list.hidden);
        });
        for (const match of matches) {
            const row = document.createElement("div");
            row.className = "wb-row wb-search-match";
            // Some context before the match, as VS Code trims long lines.
            const start = Math.max(0, match.column - 1 - 20);
            const before = (start > 0 ? "…" : "") + match.text.slice(start, match.column - 1).trimStart();
            const hit = match.text.slice(match.column - 1, match.column - 1 + match.length);
            const after = match.text.slice(match.column - 1 + match.length, match.column - 1 + match.length + 200);
            // One inline box: as flex items the text nodes would lose their outer spaces.
            const preview = document.createElement("span");
            preview.className = "wb-search-preview";
            if (writable) {
                const removed = document.createElement("del");
                removed.textContent = hit;
                const inserted = document.createElement("ins");
                inserted.textContent = this.#replacement(match);
                preview.append(before, removed, inserted, after);
            } else {
                const mark = document.createElement("mark");
                mark.textContent = hit;
                preview.append(before, mark, after);
            }
            row.append(
                preview,
                rowActions([
                    ...(writable ? [action("replace", "Replace", () => void this.#replaceIn(path, match))] : []),
                    action("close", "Dismiss", () => this.#dismiss(path, match)),
                ]),
            );
            row.title = `${match.line}: ${match.text.trim()}`;
            row.addEventListener("click", () => this.#host.open(path, match.line, match.column, true));
            row.addEventListener("dblclick", () => this.#host.open(path, match.line, match.column, false));
            list.append(row);
        }
        group.append(header, list);
        return group;
    }
}

function textInput(placeholder: string): HTMLInputElement {
    const input = document.createElement("input");
    input.type = "text";
    input.placeholder = placeholder;
    input.spellcheck = false;
    return input;
}

function action(icon: string, title: string, run: () => void): HTMLButtonElement {
    const button = document.createElement("button");
    button.type = "button";
    button.className = `codicon codicon-${icon}`;
    button.title = title;
    button.addEventListener("click", (event) => {
        event.stopPropagation();
        run();
    });
    button.addEventListener("dblclick", (event) => event.stopPropagation());
    return button;
}

function rowActions(buttons: HTMLButtonElement[]): HTMLElement {
    const element = document.createElement("span");
    element.className = "wb-search-actions";
    element.append(...buttons);
    return element;
}

/** Comma-separated globs that match whole path segments anywhere in the path; undefined for none. */
function globMatcher(value: string): ((path: string) => boolean) | undefined {
    const globs = value
        .split(",")
        .map((glob) => glob.trim().replace(/\/+$/, ""))
        .filter(Boolean);
    if (globs.length === 0) return undefined;
    const patterns = globs.map((glob) => {
        const source = glob
            .replace(/[.+^${}()|[\]\\]/g, "\\$&")
            .replace(/\*\*\//g, "\u0001")
            .replace(/\*\*/g, "\u0000")
            .replace(/\*/g, "[^/]*")
            .replace(/\?/g, "[^/]")
            .replace(/\u0001/g, "(?:.*/)?")
            .replace(/\u0000/g, ".*");
        return new RegExp(`(^|/)${source}($|/)`);
    });
    return (path) => patterns.some((pattern) => pattern.test(path));
}

/** The filter for "files to include": an empty field includes everything. */
export function includeMatcher(value: string): (path: string) => boolean {
    return globMatcher(value) ?? (() => true);
}

/** The filter for "files to exclude": an empty field excludes nothing. */
export function excludeMatcher(value: string): (path: string) => boolean {
    return globMatcher(value) ?? (() => false);
}

/**
 * The text that replaces `match`. A regular expression's replacement takes
 * `$1`, `$<name>`, `$&` and `$$` as String.replace does, with the match's own
 * groups; otherwise it is taken literally, as VS Code does.
 */
export function replacementFor(match: Match, pattern: RegExp, replace: string, regex: boolean): string {
    if (!regex) return replace;
    // A sticky copy matches only at the match's column, with the same line around it.
    const sticky = new RegExp(pattern.source, pattern.flags.replace("g", "") + "y");
    sticky.lastIndex = match.column - 1;
    const replaced = match.text.replace(sticky, replace);
    const tail = match.text.length - (match.column - 1 + match.length);
    return replaced.slice(match.column - 1, replaced.length - tail);
}

/** Apply edits to `text`, skipping those whose text is not `expect`; also returns how many were applied. */
export function applyEdits(text: string, edits: readonly ReplaceEdit[]): { text: string; applied: ReplaceEdit[] } {
    const applied = [...edits]
        .sort((a, b) => a.from - b.from)
        .filter(
            (edit, i, all) =>
                text.slice(edit.from, edit.to) === edit.expect && (i === 0 || all[i - 1]!.to <= edit.from),
        );
    let out = "";
    let at = 0;
    for (const edit of applied) {
        out += text.slice(at, edit.from) + edit.insert;
        at = edit.to;
    }
    return { text: out + text.slice(at), applied };
}

/** The matches of `pattern` (global) in `text`, at most `limit` of them. */
export function find(text: string, pattern: RegExp, limit = 1000): Match[] {
    const matches: Match[] = [];
    const lines = text.split("\n");
    let lineStart = 0;
    for (const [index, line] of lines.entries()) {
        pattern.lastIndex = 0;
        for (let m = pattern.exec(line); m !== null; m = pattern.exec(line)) {
            if (m[0].length === 0) {
                pattern.lastIndex++;
                continue;
            }
            matches.push({
                line: index + 1,
                column: m.index + 1,
                length: m[0].length,
                offset: lineStart + m.index,
                text: line,
            });
            if (matches.length >= limit) return matches;
        }
        lineStart += line.length + 1;
    }
    return matches;
}
